import { ConvexError } from "convex/values"

/**
 * Convex only forwards a `ConvexError`'s payload to the browser. A plain
 * `Error` thrown by a server function has its text replaced with "Server
 * Error" in production, so `error.message` from a Convex call is never
 * something a person can act on — read `error.data` instead.
 *
 * The client library stamps every error it raises with a `[CONVEX …]` or
 * `[Request ID: …]` prefix, which is what lets this tell a redacted server
 * message from a genuine browser-side one (a failed dynamic import, a file
 * that could not be read) that is still worth showing.
 */
const CONVEX_PREFIX = /^\[(CONVEX |Request ID: )/

/**
 * The one payload deliberately withheld. Better Auth raises "Unauthenticated"
 * when Convex has not finished authenticating the connection yet, so it is a
 * symptom of the ungated-`ensureProfile` race rather than a decision anyone
 * made about this request — showing it would just teach users a new word for
 * "try again". Every other rejection, authorization ones included, is meant to
 * reach the reader; do not grow this set to quiet a message you find terse.
 */
const PLUMBING_PAYLOADS = new Set(["Unauthenticated"])

export function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ConvexError) {
    const { data } = error
    if (typeof data === "string" && data.trim() && !PLUMBING_PAYLOADS.has(data)) {
      return data
    }
    if (
      data &&
      typeof data === "object" &&
      "message" in data &&
      typeof (data as { message?: unknown }).message === "string"
    ) {
      return (data as { message: string }).message
    }
    return fallback
  }
  if (
    error instanceof Error &&
    error.message &&
    !CONVEX_PREFIX.test(error.message)
  ) {
    return error.message
  }
  return fallback
}
